import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toastError, toastSuccess } from '@/lib/toast';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import PageHeader from '@/components/layout/PageHeader';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import BotaoExcluirComCascata from '@/components/BotaoExcluirComCascata';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Badge from '@/components/layout/Badge';
import Modal from '@/components/layout/Modal';
import Icon from '@mdi/react';
import {
    mdiLoading,
    mdiPencil,
    mdiPlus,
    mdiTrashCan,
    mdiToggleSwitch,
    mdiToggleSwitchOffOutline,
    mdiClose,
    mdiVpn,
    mdiFileUploadOutline,
} from '@mdi/js';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoMemo from '@/components/Formulario/CampoMemo';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoListaObjetos from '@/components/Formulario/CampoListaObjetos';
import { extractApiError } from '@/lib/apiError';
import { useAppliance } from '@/lib/useAppliance';
import type { VPNDTO, VPNTipo, VPNModo, VPNOpenvpnProtocolo, VPNWireguardPeerDTO, VPNRotaDTO, VPNUsuarioDTO } from '@webip/shared/src/dto/vpn';
import type { VPNStatusDTO } from '@webip/shared/src/dto/connection_status';
import StatusDot from '@/components/layout/StatusDot';
import ConnectionLogPanel from '@/components/layout/ConnectionLogPanel';
import { formatDuration } from '@/lib/formatDuration';
import CampoMultiTexto from '@/components/Formulario/CampoMultiTexto';
import Grid, { GridItem } from '@/components/layout/Grid';

// ─── OPÇÕES ESTÁTICAS ─────────────────────────────────────────────────────────

const TIPO_OPTIONS = [
    { id: 'pptp', label: 'PPTP' },
    { id: 'l2tp', label: 'L2TP' },
    { id: 'sstp', label: 'SSTP' },
    { id: 'openvpn', label: 'OpenVPN' },
    { id: 'wireguard', label: 'WireGuard' },
];

const MODO_OPTIONS = [
    { id: 'cliente', label: 'Cliente' },
    { id: 'servidor', label: 'Servidor' },
];

const TIPO_BADGE: Record<VPNTipo, string> = {
    pptp: 'PPTP',
    l2tp: 'L2TP',
    sstp: 'SSTP',
    openvpn: 'OpenVPN',
    wireguard: 'WireGuard',
};

// Valores do RouterOS 7 (validados no backend em vpn-validacao.ts). O
// servidor aceita vários (lista separada por vírgula); o cliente, um só.
const OPENVPN_AUTH_OPTIONS = [
    { id: 'md5', label: 'MD5' },
    { id: 'sha1', label: 'SHA1' },
    { id: 'sha256', label: 'SHA256' },
    { id: 'sha384', label: 'SHA384' },
    { id: 'sha512', label: 'SHA512' },
    { id: 'null', label: 'Nenhum (null)' },
];

const OPENVPN_CIPHER_OPTIONS = [
    { id: 'blowfish128', label: 'Blowfish 128' },
    { id: 'aes128-cbc', label: 'AES 128 CBC' },
    { id: 'aes192-cbc', label: 'AES 192 CBC' },
    { id: 'aes256-cbc', label: 'AES 256 CBC' },
    { id: 'aes128-gcm', label: 'AES 128 GCM' },
    { id: 'aes192-gcm', label: 'AES 192 GCM' },
    { id: 'aes256-gcm', label: 'AES 256 GCM' },
    { id: 'null', label: 'Nenhum (null)' },
];

const OPENVPN_PROTOCOLO_OPTIONS = [
    { id: 'tcp', label: 'TCP' },
    { id: 'udp', label: 'UDP' },
];

const splitLista = (v: string) => v.split(',').map((s) => s.trim()).filter(Boolean);

// ─── TIPOS INTERNOS ───────────────────────────────────────────────────────────

type FormState = {
    nome: string;
    descricao: string;
    tipo: VPNTipo | '';
    modo: VPNModo | '';
    ativo: boolean;
    // cliente
    server_address: string;
    server_port: string;
    usuario: string;
    senha: string;
    use_ipsec: boolean;
    ipsec_secret: string;
    certificado_pem: string;
    /** Vazio em edição = mantém a chave gravada (ela nunca volta da API). */
    certificado_chave_pem: string;
    certificado_ca_pem: string;
    /** Idem `certificado_chave_pem`, para a CA (autoassinada). */
    certificado_ca_chave_pem: string;
    openvpn_auth: string;
    openvpn_cipher: string;
    openvpn_protocolo: VPNOpenvpnProtocolo;
    // wireguard
    wg_private_key: string;
    wg_listen_port: string;
    wg_preshared_key: string;
    // shared
    rotas: VPNRotaDTO[];
    // servidor
    porta: string;
    ip_pool: string;
    dns: string; // newline-separated
    // sub-resources (edited inline via separate state)
};

const EMPTY_FORM: FormState = {
    nome: '',
    descricao: '',
    tipo: '',
    modo: '',
    ativo: true,
    server_address: '',
    server_port: '',
    usuario: '',
    senha: '',
    use_ipsec: false,
    ipsec_secret: '',
    certificado_pem: '',
    certificado_chave_pem: '',
    certificado_ca_pem: '',
    certificado_ca_chave_pem: '',
    openvpn_auth: '',
    openvpn_cipher: '',
    openvpn_protocolo: 'tcp',
    wg_private_key: '',
    wg_listen_port: '',
    wg_preshared_key: '',
    rotas: [],
    porta: '',
    ip_pool: '',
    dns: '',
};

function formFromDTO(dto: VPNDTO): FormState {
    return {
        nome: dto.nome ?? '',
        descricao: dto.descricao ?? '',
        tipo: dto.tipo ?? '',
        modo: dto.modo ?? '',
        ativo: dto.ativo ?? true,
        server_address: dto.server_address ?? '',
        server_port: dto.server_port ? String(dto.server_port) : '',
        usuario: dto.usuario ?? '',
        senha: dto.senha ?? '',
        use_ipsec: dto.use_ipsec ?? false,
        ipsec_secret: dto.ipsec_secret ?? '',
        certificado_pem: dto.certificado_pem ?? '',
        certificado_chave_pem: '',
        certificado_ca_pem: dto.certificado_ca_pem ?? '',
        certificado_ca_chave_pem: '',
        openvpn_auth: dto.openvpn_auth ?? '',
        openvpn_cipher: dto.openvpn_cipher ?? '',
        openvpn_protocolo: dto.openvpn_protocolo ?? 'tcp',
        wg_private_key: dto.wg_private_key ?? '',
        wg_listen_port: dto.wg_listen_port ? String(dto.wg_listen_port) : '',
        wg_preshared_key: dto.wg_preshared_key ?? '',
        rotas: dto.rotas ?? [],
        porta: dto.porta ? String(dto.porta) : '',
        ip_pool: dto.ip_pool ?? '',
        dns: (dto.dns ?? []).join('\n'),
    };
}

function formToBody(form: FormState, applianceId: number): Partial<VPNDTO> {
    const usaCertificado = form.tipo === 'sstp' || form.tipo === 'openvpn';
    const certificadoPem = usaCertificado ? form.certificado_pem.trim() : '';
    return {
        appliance_id: applianceId,
        nome: form.nome.trim(),
        descricao: form.descricao.trim() || undefined,
        tipo: form.tipo as VPNTipo,
        modo: form.modo as VPNModo,
        ativo: form.ativo,
        server_address: form.server_address.trim() || undefined,
        server_port: form.server_port ? Number(form.server_port) : undefined,
        usuario: form.usuario.trim() || undefined,
        senha: form.senha.trim() || undefined,
        use_ipsec: form.use_ipsec || undefined,
        ipsec_secret: form.ipsec_secret.trim() || undefined,
        certificado_pem: certificadoPem || undefined,
        certificado_chave_pem: (usaCertificado && form.certificado_chave_pem.trim()) || undefined,
        certificado_ca_pem: (usaCertificado && form.certificado_ca_pem.trim()) || undefined,
        certificado_ca_chave_pem: (usaCertificado && form.certificado_ca_chave_pem.trim()) || undefined,
        // Servidor manda a lista; cliente aceita um valor só (sobra de lista
        // quando o modo foi trocado de servidor para cliente).
        openvpn_auth: (form.modo === 'cliente' ? splitLista(form.openvpn_auth)[0] : form.openvpn_auth) || undefined,
        openvpn_cipher: (form.modo === 'cliente' ? splitLista(form.openvpn_cipher)[0] : form.openvpn_cipher) || undefined,
        openvpn_protocolo: form.tipo === 'openvpn' && form.modo === 'servidor' ? form.openvpn_protocolo : undefined,
        wg_private_key: form.wg_private_key.trim() || undefined,
        wg_listen_port: form.wg_listen_port ? Number(form.wg_listen_port) : undefined,
        wg_preshared_key: form.wg_preshared_key.trim() || undefined,
        rotas: form.rotas.filter((r) => r.cidr.trim() !== ''),
        porta: form.porta ? Number(form.porta) : undefined,
        ip_pool: form.ip_pool.trim() || undefined,
        dns: form.dns.split(/[\n,\s]+/).map((s) => s.trim()).filter(Boolean),
    };
}

function validateForm(form: FormState): string | null {
    if (!form.nome.trim()) return 'Nome é obrigatório';
    if (!form.tipo) return 'Tipo é obrigatório';
    if (!form.modo) return 'Modo é obrigatório';
    if (form.modo === 'cliente' && !form.server_address.trim()) return 'Endereço do servidor é obrigatório para modo Cliente';
    return null;
}

// ─── CAMPO PEM ────────────────────────────────────────────────────────────────

/** Memo para PEM com botão que carrega o conteúdo de um arquivo .pem/.crt/.key. */
function CampoPem({
    label,
    value,
    onChange,
    placeholder,
    hint,
    error,
}: Readonly<{
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    hint?: string;
    error?: string;
}>) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const carregar = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => onChange(String(ev.target?.result ?? ''));
        reader.readAsText(file);
    };
    return (
        <VBox style={{ gap: '4px' }}>
            <CampoMemo
                label={label}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                hint={hint}
                error={error}
                rows={4}
                spellCheck={false}
            />
            <HBox style={{ justifyContent: 'flex-end' }}>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".pem,.crt,.cer,.key"
                    style={{ display: 'none' }}
                    onChange={carregar}
                />
                <Botao size="sm" variant="outline-secondary" icon={mdiFileUploadOutline} onClick={() => inputRef.current?.click()}>
                    Carregar arquivo
                </Botao>
            </HBox>
        </VBox>
    );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────

export default function VPNPage() {
    const qc = useQueryClient();
    const { applianceId: selectedApplianceId, selectedAppliance } = useAppliance();

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<VPNDTO | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formError, setFormError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [togglingId, setTogglingId] = useState<number | null>(null);

    // Peers WireGuard para o modal em edição
    const [wgPeers, setWgPeers] = useState<VPNWireguardPeerDTO[]>([]);

    // Usuários PPP para o modal em edição (servidor pptp/l2tp/sstp/openvpn)
    const [pppUsuarios, setPppUsuarios] = useState<VPNUsuarioDTO[]>([]);

    // ── queries ───────────────────────────────────────────────────────────────

    const queryVPNs = useQuery({
        queryKey: ['vpns', selectedApplianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: VPNDTO[] }>(
                `/api/vpn?appliance_id=${selectedApplianceId}`,
                { withCredentials: true }
            );
            return res.data.result ?? [];
        },
        enabled: !!selectedApplianceId,
    });

    const queryStatus = useQuery({
        queryKey: ['vpn-status', selectedApplianceId],
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; agentOnline: boolean; result: VPNStatusDTO[] }>(
                `/api/vpn/status?appliance_id=${selectedApplianceId}`,
                { withCredentials: true }
            );
            return res.data;
        },
        enabled: !!selectedApplianceId,
        refetchInterval: 15_000,
    });

    const statusMap = new Map<number, VPNStatusDTO>(
        (queryStatus.data?.result ?? []).map((s) => [s.vpn_id, s])
    );

    // ── mutations ─────────────────────────────────────────────────────────────

    const mutCreate = useMutation({
        mutationFn: async (body: object) =>
            axios.post('/api/vpn', body, { withCredentials: true }),
        onSuccess: () => {
            qc.invalidateQueries(['vpns', selectedApplianceId]);
            toastSuccess('VPN criada com sucesso!');
            handleCloseModal();
        },
        onError: (err: unknown) => {
            const { message, fieldErrors } = extractApiError(err, 'Erro ao criar VPN');
            setFieldErrors(fieldErrors ?? {});
            toastError(message);
        },
    });

    const mutUpdate = useMutation({
        mutationFn: async ({ id, body }: { id: number; body: object }) =>
            axios.put(`/api/vpn/${id}`, body, { withCredentials: true }),
        onSuccess: () => {
            qc.invalidateQueries(['vpns', selectedApplianceId]);
            toastSuccess('VPN atualizada com sucesso!');
            handleCloseModal();
        },
        onError: (err: unknown) => {
            const { message, fieldErrors } = extractApiError(err, 'Erro ao atualizar VPN');
            setFieldErrors(fieldErrors ?? {});
            toastError(message);
        },
    });

    const mutDelete = useMutation({
        mutationFn: async (id: number) =>
            axios.delete(`/api/vpn/${id}`, { withCredentials: true }),
        onSuccess: () => {
            qc.invalidateQueries(['vpns', selectedApplianceId]);
            toastSuccess('VPN removida com sucesso!');
        },
        onError: (err: unknown) => {
            const { message } = extractApiError(err, 'Erro ao remover VPN');
            toastError(message);
        },
    });

    const handleToggle = async (vpn: VPNDTO) => {
        if (togglingId) return;
        setTogglingId(vpn.id!);
        try {
            await axios.patch(`/api/vpn/${vpn.id}/toggle`, {}, { withCredentials: true });
            qc.invalidateQueries(['vpns', selectedApplianceId]);
        } catch (err: unknown) {
            const { message } = extractApiError(err, 'Erro ao alterar estado');
            toastError(message);
        } finally {
            setTogglingId(null);
        }
    };

    // ── modal ─────────────────────────────────────────────────────────────────

    const handleOpenCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setWgPeers([]);
        setPppUsuarios([]);
        setFormError(null);
        setFieldErrors({});
        setModalOpen(true);
    };

    const handleOpenEdit = (vpn: VPNDTO) => {
        setEditing(vpn);
        setForm(formFromDTO(vpn));
        setWgPeers(vpn.wg_peers ?? []);
        setPppUsuarios(vpn.usuarios ?? []);
        setFormError(null);
        setFieldErrors({});
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditing(null);
        setFormError(null);
    };

    const handleSubmit = () => {
        if (!selectedApplianceId) {
            setFormError('Nenhuma appliance selecionada');
            return;
        }
        const error = validateForm(form);
        if (error) {
            setFormError(error);
            return;
        }
        const isPPPServidor = form.modo === 'servidor' && ['pptp', 'l2tp', 'sstp', 'openvpn'].includes(form.tipo as string);

        if (isPPPServidor) {
            const valid = pppUsuarios.filter((u) => u.nome.trim() && u.senha.trim());
            const missing = valid.find((u) => !u.local_address?.trim());
            if (missing) {
                setFormError(`Usuário "${missing.nome}": IP local do servidor é obrigatório`);
                return;
            }
        }

        setFormError(null);

        const body = {
            ...formToBody(form, Number(selectedApplianceId)),
            wg_peers: isWireGuardServidor ? wgPeers : undefined,
            usuarios: isPPPServidor ? pppUsuarios.filter((u) => u.nome.trim() && u.senha.trim()) : undefined,
        };

        if (editing) {
            mutUpdate.mutate({ id: editing.id!, body });
        } else {
            mutCreate.mutate(body);
        }
    };

    // ── helpers ───────────────────────────────────────────────────────────────

    const isCliente = form.modo === 'cliente';
    const isServidor = form.modo === 'servidor';
    const isWireGuard = form.tipo === 'wireguard';
    const isPPP = ['pptp', 'l2tp', 'sstp', 'openvpn'].includes(form.tipo as string);
    const isL2TP = form.tipo === 'l2tp';
    const isWireGuardServidor = isServidor && isWireGuard;

    // ── peers WireGuard ───────────────────────────────────────────────────────

    const addPeer = () => {
        setWgPeers((prev) => [...prev, { nome: '', public_key: '', allowed_addresses: [] }]);
    };

    const removePeer = (i: number) => {
        setWgPeers((prev) => prev.filter((_, idx) => idx !== i));
    };

    const updatePeer = (i: number, patch: Partial<VPNWireguardPeerDTO>) => {
        setWgPeers((prev) => {
            const next = [...prev];
            next[i] = { ...next[i], ...patch };
            return next;
        });
    };

    // ── usuários PPP ──────────────────────────────────────────────────────────

    const addPPPUsuario = () => {
        setPppUsuarios((prev) => [...prev, { nome: '', senha: '', ativo: true }]);
    };

    const removePPPUsuario = (i: number) => {
        setPppUsuarios((prev) => prev.filter((_, idx) => idx !== i));
    };

    const updatePPPUsuario = (i: number, patch: Partial<VPNUsuarioDTO>) => {
        setPppUsuarios((prev) => {
            const next = [...prev];
            next[i] = { ...next[i], ...patch };
            return next;
        });
    };

    // ── tabela ────────────────────────────────────────────────────────────────

    const headers: DataTableHeader[] = [
        { label: 'Nome', column: 'nome', width: '22%' },
        {
            label: 'Tipo',
            width: '100px',
            alignCenter: true,
            render: (row: VPNDTO) => <Badge variant="info">{TIPO_BADGE[row.tipo]}</Badge>,
        },
        {
            label: 'Modo',
            width: '90px',
            alignCenter: true,
            render: (row: VPNDTO) => (
                <Badge variant={row.modo === 'servidor' ? 'warning' : 'secondary'}>
                    {row.modo === 'servidor' ? 'Servidor' : 'Cliente'}
                </Badge>
            ),
        },
        {
            label: 'Conexão',
            width: '180px',
            render: (row: VPNDTO) => {
                const st = statusMap.get(row.id!);
                if (!st) return <StatusDot status="unknown" />;
                const extras: string[] = [];
                if (st.modo === 'servidor' && st.connected_clients != null) {
                    extras.push(`${st.connected_clients} cliente(s)`);
                }
                if (st.uptime_seconds != null) extras.push(`há ${formatDuration(st.uptime_seconds)}`);
                if (st.disconnect_count) extras.push(`${st.disconnect_count} desconexão(ões)`);
                return (
                    <VBox style={{ gap: '2px', alignItems: 'flex-start' }}>
                        <StatusDot status={st.status} />
                        {extras.length > 0 && (
                            <span style={{ fontSize: '11px', color: 'var(--color-muted)' }}>
                                {extras.join(' • ')}
                            </span>
                        )}
                    </VBox>
                );
            },
        },
        {
            label: 'Rotas',
            width: '70px',
            alignCenter: true,
            render: (row: VPNDTO) =>
                (row.rotas ?? []).length > 0 ? (
                    <Badge variant="secondary">{(row.rotas ?? []).length}</Badge>
                ) : (
                    <span style={{ color: 'var(--color-muted)' }}>—</span>
                ),
        },
        {
            label: 'Ativo',
            width: '70px',
            alignCenter: true,
            render: (row: VPNDTO) => (
                <Botao
                    size="sm"
                    variant="none-primary"
                    icon={row.ativo ? mdiToggleSwitch : mdiToggleSwitchOffOutline}
                    iconProps={{ color: row.ativo ? 'var(--color-success)' : 'var(--color-muted)' }}
                    disabled={togglingId === row.id}
                    onClick={() => handleToggle(row)}
                />
            ),
        },
        {
            label: 'Ações',
            width: '110px',
            stickRight: true,
            alignRight: true,
            render: (row: VPNDTO) => (
                <HBox style={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <Botao
                        size="sm"
                        variant="outline-primary"
                        icon={mdiPencil}
                        onClick={() => handleOpenEdit(row)}
                        title="Editar VPN"
                    />
                    <BotaoExcluirComCascata
                        tipo="vpn"
                        id={row.id!}
                        variant="outline-danger"
                        onExcluido={() => {
                            qc.invalidateQueries(['vpns', selectedApplianceId]);
                            toastSuccess('VPN removida com sucesso!');
                        }}
                        onErro={(msg) => toastError(msg)}
                    />
                </HBox>
            ),
        },
    ];

    const vpns = queryVPNs.data ?? [];
    const isSaving = mutCreate.isLoading || mutUpdate.isLoading;

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <LayoutUsuario>
            <VBox style={{ padding: '24px', gap: '16px', height: '100%', overflow: 'hidden' }}>
                <PageHeader
                    title={`VPNs${selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}`}
                    description="Gerencie conexões VPN (clientes e servidores) sincronizadas com o MikroTik"
                    actions={(
                        <Botao
                            variant="primary"
                            icon={mdiPlus}
                            onClick={handleOpenCreate}
                            disabled={!selectedApplianceId}
                        >
                            Nova VPN
                        </Botao>
                    )}
                />

                {/* Sem appliance */}
                {!selectedApplianceId && (
                    <VBox style={{ alignItems: 'center', padding: '48px', color: 'var(--color-muted)', gap: '8px' }}>
                        <Icon path={mdiVpn} size={2} />
                        <span>Selecione uma appliance no menu lateral para gerenciar as VPNs</span>
                    </VBox>
                )}

                {selectedApplianceId && queryVPNs.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                        <Icon path={mdiLoading} spin size={2} />
                    </HBox>
                )}

                {selectedApplianceId && queryVPNs.isError && (
                    <p style={{ color: 'var(--color-danger)' }}>Erro ao carregar VPNs. Tente novamente.</p>
                )}

                {selectedApplianceId && !queryVPNs.isLoading && !queryVPNs.isError && (
                    <VBox style={{ gap: '16px', overflow: 'auto' }}>
                        <DataTable cabecalhos={headers} linhas={vpns} />
                        {vpns.length > 0 && (
                            <ConnectionLogPanel
                                applianceId={Number(selectedApplianceId)}
                                topics={['ppp', 'pptp', 'l2tp', 'sstp', 'ovpn', 'wireguard']}
                                title="Logs de VPN"
                            />
                        )}
                    </VBox>
                )}
            </VBox>

            {/* ── Modal criar / editar ─────────────────────────────────────── */}
            <Modal
                show={modalOpen}
                title={editing ? 'Editar VPN' : 'Nova VPN'}
                onClose={handleCloseModal}
                onCancel={handleCloseModal}
                onConfirm={handleSubmit}
                confirmProps={{ label: editing ? 'Salvar' : 'Criar', disabled: isSaving }}
                cancelProps={{ label: 'Cancelar', disabled: isSaving }}
                size="lg"
            >
                <VBox style={{ gap: '14px' }}>
                    {/* Nome e Descrição */}
                    <HBox style={{ gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                            <CampoTexto
                                label="Nome"
                                value={form.nome}
                                onChange={(v: string) => setForm((f) => ({ ...f, nome: v }))}
                                placeholder="Ex: VPN-Matriz"
                                required
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CampoTexto
                                label="Descrição"
                                value={form.descricao}
                                onChange={(v: string) => setForm((f) => ({ ...f, descricao: v }))}
                                placeholder="Opcional"
                            />
                        </div>
                    </HBox>

                    {/* Tipo e Modo */}
                    <HBox style={{ gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                            <CampoSeletor
                                label="Tipo"
                                value={form.tipo || null}
                                onChange={(v) => setForm((f) => ({ ...f, tipo: (v ?? '') as VPNTipo }))}
                                options={TIPO_OPTIONS}
                                required
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <CampoSeletor
                                label="Modo"
                                value={form.modo || null}
                                onChange={(v) => setForm((f) => ({ ...f, modo: (v ?? '') as VPNModo }))}
                                options={MODO_OPTIONS}
                                required
                            />
                        </div>
                    </HBox>

                    {/* ── CLIENTE: endereço do servidor ── */}
                    {isCliente && (
                        <>
                            <HBox style={{ gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    <CampoTexto
                                        label="Endereço do servidor"
                                        value={form.server_address}
                                        onChange={(v: string) => setForm((f) => ({ ...f, server_address: v }))}
                                        placeholder="Ex: vpn.empresa.com ou 203.0.113.1"
                                        required
                                    />
                                </div>
                                {(isPPP || isWireGuard) && (
                                    <div style={{ width: '120px' }}>
                                        <CampoNumero
                                            label="Porta"
                                            value={form.server_port ? Number(form.server_port) : null}
                                            onChange={(v) => setForm((f) => ({ ...f, server_port: v != null ? String(v) : '' }))}
                                            placeholder="Padrão"
                                        />
                                    </div>
                                )}
                            </HBox>

                            {/* Credenciais PPP */}
                            {isPPP && (
                                <HBox style={{ gap: '12px' }}>
                                    <div style={{ flex: 1 }}>
                                        <CampoTexto
                                            label="Usuário"
                                            value={form.usuario}
                                            onChange={(v: string) => setForm((f) => ({ ...f, usuario: v }))}
                                            placeholder="Usuário PPP"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <CampoTexto
                                            label="Senha"
                                            value={form.senha}
                                            onChange={(v: string) => setForm((f) => ({ ...f, senha: v }))}
                                            placeholder="Senha PPP"
                                            type="password"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                </HBox>
                            )}

                            {/* L2TP IPSec */}
                            {isL2TP && (
                                <VBox style={{ gap: '8px' }}>
                                    <HBox style={{ gap: '8px', alignItems: 'center' }}>
                                        <input
                                            type="checkbox"
                                            id="use-ipsec"
                                            checked={form.use_ipsec}
                                            onChange={(e) => setForm((f) => ({ ...f, use_ipsec: e.target.checked }))}
                                        />
                                        <label htmlFor="use-ipsec" style={{ cursor: 'pointer', userSelect: 'none' }}>
                                            Usar IPSec
                                        </label>
                                    </HBox>
                                    {form.use_ipsec && (
                                        <CampoTexto
                                            label="Segredo IPSec"
                                            value={form.ipsec_secret}
                                            onChange={(v: string) => setForm((f) => ({ ...f, ipsec_secret: v }))}
                                            placeholder="Segredo pré-compartilhado"
                                        />
                                    )}
                                </VBox>
                            )}

                            {/* Auth / Cipher OpenVPN cliente */}
                            {form.tipo === 'openvpn' && (
                                <HBox style={{ gap: '12px' }}>
                                    <div style={{ flex: 1 }}>
                                        <CampoSeletor
                                            label="Auth"
                                            value={form.openvpn_auth || null}
                                            onChange={(v) => setForm((f) => ({ ...f, openvpn_auth: v ?? '' }))}
                                            options={OPENVPN_AUTH_OPTIONS}
                                            error={fieldErrors.openvpn_auth}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <CampoSeletor
                                            label="Cipher"
                                            value={form.openvpn_cipher || null}
                                            onChange={(v) => setForm((f) => ({ ...f, openvpn_cipher: v ?? '' }))}
                                            options={OPENVPN_CIPHER_OPTIONS}
                                            error={fieldErrors.openvpn_cipher}
                                        />
                                    </div>
                                </HBox>
                            )}

                            {/* WireGuard cliente */}
                            {isWireGuard && (
                                <>
                                    <CampoTexto
                                        label="Chave privada (WireGuard)"
                                        value={form.wg_private_key}
                                        onChange={(v: string) => setForm((f) => ({ ...f, wg_private_key: v }))}
                                        placeholder="Chave privada base64"
                                    />
                                    <CampoTexto
                                        label="Chave pública do servidor"
                                        value={form.wg_preshared_key}
                                        onChange={(v: string) => setForm((f) => ({ ...f, wg_preshared_key: v }))}
                                        placeholder="Chave pública do peer (servidor)"
                                    />
                                    <CampoNumero
                                        label="Porta de escuta local"
                                        value={form.wg_listen_port ? Number(form.wg_listen_port) : null}
                                        onChange={(v) => setForm((f) => ({ ...f, wg_listen_port: v != null ? String(v) : '' }))}
                                        placeholder="Ex: 51820"
                                    />
                                </>
                            )}
                        </>
                    )}

                    {/* ── SERVIDOR WireGuard ── */}
                    {isWireGuardServidor && (
                        <>
                            <HBox style={{ gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    <CampoTexto
                                        label="Chave privada"
                                        value={form.wg_private_key}
                                        onChange={(v: string) => setForm((f) => ({ ...f, wg_private_key: v }))}
                                        placeholder="Chave privada base64"
                                    />
                                </div>
                                <div style={{ width: '140px' }}>
                                    <CampoNumero
                                        label="Porta de escuta"
                                        value={form.wg_listen_port ? Number(form.wg_listen_port) : null}
                                        onChange={(v) => setForm((f) => ({ ...f, wg_listen_port: v != null ? String(v) : '' }))}
                                        placeholder="51820"
                                    />
                                </div>
                            </HBox>

                            {/* Peers WireGuard */}
                            <VBox style={{ gap: '8px' }}>
                                <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={{ fontWeight: 500, fontSize: '14px' }}>Peers WireGuard</label>
                                    <Botao size="sm" variant="outline-primary" icon={mdiPlus} onClick={addPeer}>
                                        Adicionar Peer
                                    </Botao>
                                </HBox>
                                {wgPeers.length === 0 && (
                                    <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                                        Nenhum peer configurado.
                                    </p>
                                )}
                                {wgPeers.map((peer, i) => (
                                    <VBox key={i} style={{ gap: '8px', padding: '10px', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
                                        <HBox style={{ gap: '8px', alignItems: 'flex-start' }}>
                                            <div style={{ flex: 1 }}>
                                                <CampoTexto
                                                    label="Nome do peer"
                                                    value={peer.nome}
                                                    onChange={(v: string) => updatePeer(i, { nome: v })}
                                                    placeholder="Ex: Escritório SP"
                                                />
                                            </div>
                                            <Botao
                                                size="sm"
                                                variant="outline-danger"
                                                icon={mdiClose}
                                                onClick={() => removePeer(i)}
                                                style={{ marginTop: '22px' }}
                                            />
                                        </HBox>
                                        <CampoTexto
                                            label="Chave pública"
                                            value={peer.public_key}
                                            onChange={(v: string) => updatePeer(i, { public_key: v })}
                                            placeholder="Chave pública base64"
                                        />
                                        <CampoTexto
                                            label="Endereços permitidos (um por linha)"
                                            value={(peer.allowed_addresses ?? []).join('\n')}
                                            onChange={(v: string) =>
                                                updatePeer(i, {
                                                    allowed_addresses: v.split('\n').map((s) => s.trim()).filter(Boolean),
                                                })
                                            }
                                            placeholder={'Ex: 10.0.0.2/32\n192.168.1.0/24'}
                                            multiline
                                            rows={2}
                                        />
                                        <HBox style={{ gap: '12px' }}>
                                            <div style={{ flex: 1 }}>
                                                <CampoTexto
                                                    label="Endpoint (opcional)"
                                                    value={peer.endpoint_address ?? ''}
                                                    onChange={(v: string) => updatePeer(i, { endpoint_address: v })}
                                                    placeholder="IP ou hostname do peer"
                                                />
                                            </div>
                                            <div style={{ width: '100px' }}>
                                                <CampoNumero
                                                    label="Porta"
                                                    value={peer.endpoint_port ?? null}
                                                    onChange={(v) => updatePeer(i, { endpoint_port: v ?? undefined })}
                                                    placeholder="51820"
                                                />
                                            </div>
                                            <div style={{ width: '120px' }}>
                                                <CampoNumero
                                                    label="Keepalive (s)"
                                                    value={peer.persistent_keepalive ?? null}
                                                    onChange={(v) => updatePeer(i, { persistent_keepalive: v ?? undefined })}
                                                    placeholder="25"
                                                />
                                            </div>
                                        </HBox>
                                    </VBox>
                                ))}
                            </VBox>
                        </>
                    )}

                    {/* ── SERVIDOR PPP: configuração de pool/rede ── */}
                    {isServidor && isPPP && (
                        <>
                            <HBox style={{ gap: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    <CampoNumero
                                        label="Porta do servidor"
                                        value={form.porta ? Number(form.porta) : null}
                                        onChange={(v) => setForm((f) => ({ ...f, porta: v != null ? String(v) : '' }))}
                                        placeholder={form.tipo === 'openvpn' ? '1194' : 'Padrão do protocolo'}
                                        error={fieldErrors.porta}
                                    />
                                </div>
                                {form.tipo === 'openvpn' && (
                                    <div style={{ flex: 1 }}>
                                        <CampoSeletor
                                            label="Protocolo"
                                            value={form.openvpn_protocolo}
                                            onChange={(v) => setForm((f) => ({ ...f, openvpn_protocolo: (v as VPNOpenvpnProtocolo) ?? 'tcp' }))}
                                            options={OPENVPN_PROTOCOLO_OPTIONS}
                                            error={fieldErrors.openvpn_protocolo}
                                        />
                                    </div>
                                )}
                                <div style={{ flex: 1 }}>
                                    <CampoTexto
                                        label="Pool de IPs"
                                        value={form.ip_pool}
                                        onChange={(v: string) => setForm((f) => ({ ...f, ip_pool: v }))}
                                        placeholder="Ex: 10.0.0.2-10.0.0.100"
                                    />
                                </div>
                            </HBox>
                            <CampoTexto
                                label="DNS (separados por espaço, vírgula ou linha)"
                                value={form.dns}
                                onChange={(v: string) => setForm((f) => ({ ...f, dns: v }))}
                                placeholder={'8.8.8.8 1.1.1.1'}
                                multiline
                                rows={2}
                            />
                            {/* Auth / Cipher OpenVPN servidor — aceita vários; o cliente negocia um deles */}
                            {form.tipo === 'openvpn' && (
                                <HBox style={{ gap: '12px' }}>
                                    <div style={{ flex: 1 }}>
                                        <CampoMultiSeletor
                                            label="Auth"
                                            placeholder="Padrão do RouterOS"
                                            value={splitLista(form.openvpn_auth)}
                                            onChange={(v: string[]) => setForm((f) => ({ ...f, openvpn_auth: v.join(',') }))}
                                            options={OPENVPN_AUTH_OPTIONS}
                                            error={fieldErrors.openvpn_auth}
                                            noSearch
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <CampoMultiSeletor
                                            label="Cipher"
                                            placeholder="Padrão do RouterOS"
                                            value={splitLista(form.openvpn_cipher)}
                                            onChange={(v: string[]) => setForm((f) => ({ ...f, openvpn_cipher: v.join(',') }))}
                                            options={OPENVPN_CIPHER_OPTIONS}
                                            error={fieldErrors.openvpn_cipher}
                                            noSearch
                                        />
                                    </div>
                                </HBox>
                            )}
                        </>
                    )}

                    {/* ── Certificado SSTP / OpenVPN (cliente e servidor) ── */}
                    {(isCliente || isServidor) && (form.tipo === 'sstp' || form.tipo === 'openvpn') && (
                        <VBox style={{ gap: '10px', padding: '10px', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
                            <label style={{ fontWeight: 500, fontSize: '14px' }}>Certificado</label>
                            <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                                Cole o PEM ou carregue o arquivo. No próximo sync o agente importa em
                                System › Certificates do MikroTik e vincula {isServidor ? 'ao servidor' : 'à interface'}.
                            </p>
                            <Grid>
                                <GridItem md={12} lg={6}>
                                    <CampoPem
                                        label="Certificado (PEM)"
                                        value={form.certificado_pem}
                                        onChange={(v) => setForm((f) => ({ ...f, certificado_pem: v }))}
                                        placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                                        error={fieldErrors.certificado_pem}
                                    />
                                </GridItem>
                                <GridItem md={12} lg={6}>
                                    <CampoPem
                                        label="Chave privada do certificado (PEM, sem senha)"
                                        value={form.certificado_chave_pem}
                                        onChange={(v) => setForm((f) => ({ ...f, certificado_chave_pem: v }))}
                                        placeholder={
                                            editing?.certificado_chave_definida
                                                ? 'Chave gravada — deixe em branco para manter'
                                                : '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'
                                        }
                                        hint={
                                            editing?.certificado_chave_definida
                                                ? 'Por segurança a chave gravada não é exibida.'
                                                : 'Obrigatória junto com o certificado.'
                                        }
                                        error={fieldErrors.certificado_chave_pem}
                                    />
                                </GridItem>
                                <GridItem md={12} lg={6}>
                                    <CampoPem
                                        label="CA (PEM)"
                                        value={form.certificado_ca_pem}
                                        onChange={(v) => setForm((f) => ({ ...f, certificado_ca_pem: v }))}
                                        placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                                        hint="Opcional. Aceita a cadeia inteira (intermediárias + raiz)."
                                        error={fieldErrors.certificado_ca_pem}
                                    />
                                </GridItem>
                                <GridItem md={12} lg={6}>
                                    <CampoPem
                                        label="Chave privada da CA (PEM, sem senha)"
                                        value={form.certificado_ca_chave_pem}
                                        onChange={(v) => setForm((f) => ({ ...f, certificado_ca_chave_pem: v }))}
                                        placeholder={
                                            editing?.certificado_ca_chave_definida
                                                ? 'Chave gravada — deixe em branco para manter'
                                                : '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'
                                        }
                                        hint={
                                            editing?.certificado_ca_chave_definida
                                                ? 'Por segurança a chave gravada não é exibida.'
                                                : 'Opcional. Para CA autoassinada: permite ao MikroTik assinar certificados de cliente.'
                                        }
                                        error={fieldErrors.certificado_ca_chave_pem}
                                    />
                                </GridItem>
                            </Grid>
                        </VBox>
                    )}

                    {/* ── SERVIDOR PPP: usuários locais ── */}
                    {isServidor && isPPP && (
                        <VBox style={{ gap: '8px' }}>
                            <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>Usuários PPP</label>
                                <Botao size="sm" variant="outline-primary" icon={mdiPlus} onClick={addPPPUsuario}>
                                    Adicionar Usuário
                                </Botao>
                            </HBox>
                            {pppUsuarios.length === 0 && (
                                <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                                    Nenhum usuário configurado.
                                </p>
                            )}
                            {pppUsuarios.map((u, i) => (
                                <HBox>
                                    <Grid key={i}>
                                        <GridItem sm={12} md={6} lg={3}>
                                            <CampoTexto
                                                label="Usuário (login PPP)"
                                                value={u.nome}
                                                onChange={(v: string) => updatePPPUsuario(i, { nome: v })}
                                                placeholder="Ex: filial1"
                                                autoComplete="off"
                                            />
                                        </GridItem>
                                        <GridItem sm={12} md={6} lg={3}>
                                            <CampoTexto
                                                label="Senha"
                                                value={u.senha}
                                                onChange={(v: string) => updatePPPUsuario(i, { senha: v })}
                                                placeholder="Senha PPP"
                                                type="password"
                                                autoComplete="new-password"
                                            />
                                        </GridItem>

                                        <GridItem sm={12} md={6} lg={3}>
                                            <CampoTexto
                                                label="IP local do servidor"
                                                value={u.local_address ?? ''}
                                                onChange={(v: string) => updatePPPUsuario(i, { local_address: v.trim() || undefined })}
                                                placeholder="Ex: 10.0.0.1"
                                                required
                                            />
                                        </GridItem>
                                        <GridItem sm={12} md={6} lg={3}>
                                            <CampoTexto
                                                label="IP do cliente (opcional)"
                                                value={u.remote_address ?? ''}
                                                onChange={(v: string) => updatePPPUsuario(i, { remote_address: v.trim() || undefined })}
                                                placeholder="Ex: 10.0.0.2"
                                            />
                                        </GridItem>
                                        <GridItem sm={12}>
                                            <CampoMultiTexto
                                                label="Redes acessíveis via este túnel (uma por linha)"
                                                value={(u.rotas ?? [])}
                                                onChange={(v: string[]) =>
                                                    updatePPPUsuario(i, {
                                                        rotas: v.map((s) => s.trim()).filter(Boolean),
                                                    })
                                                }
                                                placeholder={'Ex: 192.168.1.0/24\n10.10.0.0/16'}
                                                multiline
                                                rows={2}
                                            />
                                        </GridItem>
                                    </Grid>
                                    <Botao
                                        size="sm"
                                        variant="outline-danger"
                                        icon={mdiClose}
                                        onClick={() => removePPPUsuario(i)}
                                        style={{ marginTop: '22px' }}
                                    />
                                </HBox>
                            ))}
                        </VBox>
                    )}

                    {/* ── Rotas de acesso (apenas modo cliente) ── */}
                    {isCliente && <CampoListaObjetos<VPNRotaDTO>
                        label="Rotas de acesso"
                        value={form.rotas}
                        onChange={(v) => setForm((f) => ({ ...f, rotas: v }))}
                        colunas={[
                            { label: 'CIDR', campo: 'cidr', placeholder: '192.168.10.0/24' },
                            { label: 'Gateway', campo: 'gateway', placeholder: '10.0.0.1' },
                        ]}
                        novoItem={{ cidr: '', gateway: '' }}
                        textoBotaoAdicionar="Adicionar Rota"
                        textoVazio="Nenhuma rota configurada."
                    />}

                    {/* Ativo */}
                    <HBox style={{ gap: '8px', alignItems: 'center' }}>
                        <input
                            type="checkbox"
                            id="vpn-ativo"
                            checked={form.ativo}
                            onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
                        />
                        <label htmlFor="vpn-ativo" style={{ cursor: 'pointer', userSelect: 'none' }}>
                            VPN ativa
                        </label>
                    </HBox>

                    {/* Erro de validação */}
                    {formError && (
                        <p style={{ color: 'var(--color-danger)', fontSize: '13px', margin: 0 }}>{formError}</p>
                    )}
                </VBox>
            </Modal>
        </LayoutUsuario>
    );
}
